'use client';

import { useState } from 'react';

import { proxyFetch } from '@ps/functions/utils/proxyFetch';
import { useCurrentUserId } from '@ps/ui/contexts/auth';

import { useIsFollowing } from './useIsFollowing';

/**
 * Follow state + follow/unfollow actions for the free FOLLOWER tier. `isFollowing`
 * is the reactive Firestore truth (`profiles/{uid}/followings/{creatorId}` via
 * {@link useIsFollowing}) — 1:1 with PopshopRN's `isFollowing$`, so the state is
 * correct on any surface (bio, dashboard, /my-account), not just inside a
 * `BioViewerProvider`.
 *
 * - follow  → `userApiControllerCreateFollower` (same call `FollowGuardModal` /
 *   the checkout `createFollower` step use).
 * - unfollow → `userApiControllerRemoveFollower({ userId: creatorId,
 *   followerId: currentUserId })`, matching RN's `useUnfollowFn`. (Users can't
 *   unfollow themselves — RN guards the same way.)
 *
 * Both mutations just write the Firestore follow doc; `isFollowing` flips
 * reactively, so we don't hold a local optimistic copy.
 */
export function useFollowCreator(creatorId: string) {
  const userId = useCurrentUserId();
  const isFollowing = useIsFollowing(creatorId);
  const [pending, setPending] = useState(false);

  // A signed-in, non-self viewer can follow. Anonymous viewers can't (there's
  // no session to write the follow doc for) — callers should route them to
  // sign-in rather than treat a no-op as success.
  const canPerformFollow = userId != null && userId !== creatorId;

  /** Follows the creator. Returns `false` (no-op) when the viewer can't follow
   *  (anonymous / self) so callers don't report a phantom success. */
  async function follow(consent: {
    receivePromotionalOffers: boolean;
    sharePhoneNumber: boolean;
  }): Promise<boolean> {
    if (!canPerformFollow) return false;
    setPending(true);
    try {
      await proxyFetch('/api/bio/follow', {
        method: 'POST',
        body: {
          creatorId,
          receivePromotionalOffers: consent.receivePromotionalOffers,
          sharePhoneNumber: consent.sharePhoneNumber,
        },
      });
      return true;
    } finally {
      setPending(false);
    }
  }

  async function unfollow(): Promise<void> {
    // Users can't unfollow themselves (the API errors) — RN guards identically.
    if (userId == null || userId === creatorId) return;
    setPending(true);
    try {
      const params = new URLSearchParams({ creatorId });
      await proxyFetch(`/api/bio/follow?${params.toString()}`, {
        method: 'DELETE',
      });
    } finally {
      setPending(false);
    }
  }

  return { isFollowing, canPerformFollow, pending, follow, unfollow };
}
